import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");
  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }
  return `${base}${path}`;
}

function formatISTDateTime(value) {
  if (!value) return "—";

  const cleanValue = value.replace("Z", "");
  const date = new Date(cleanValue);

  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-IN", { month: "short" });
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");

  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;

  return `${day} ${month} ${year}, ${hours}:${minutes} ${ampm}`;
}

export default function InCampusList() {
  const navigate = useNavigate();
  const skin = useSkin();

  const [loading, setLoading] = useState(true);
  const [checks, setChecks] = useState([]);
  const [filteredChecks, setFilteredChecks] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    random: 0,
    schedule: 0,
    today: 0,
  });

  const [filters, setFilters] = useState({
    search: "",
    checkType: "",
    startDate: "",
    endDate: "",
  });

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }
    fetchDashboardData(token);
    fetchChecks(token);
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, checks]);

  const fetchDashboardData = async (token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        const companyDetails = data?.data?.companyDetails || {};
        setDashboardData(companyDetails);
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    }
  };

  const fetchChecks = async (token) => {
    try {
      setLoading(true);
      const token = getToken();

      const res = await fetch(api("/api/incampus/in-campus-checks"), {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error("Failed to fetch in-campus checks");
      }

      const data = await res.json();
      const checkList = data?.data || data || [];

      setChecks(checkList);
      setFilteredChecks(checkList);

      // Calculate stats
      const today = new Date().toDateString();
      const statsData = {
        total: checkList.length,
        random: checkList.filter((c) => c.check_type === "random").length,
        schedule: checkList.filter((c) => c.check_type === "schedule").length,
        today: checkList.filter((c) => {
          const checkDate = new Date(c.check_time).toDateString();
          return checkDate === today;
        }).length,
      };
      setStats(statsData);

      toast.success(`Loaded ${checkList.length} in-campus checks`);
    } catch (error) {
      console.error("Fetch checks error:", error);
      toast.error(error.message || "Failed to load in-campus checks");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...checks];

    // Search filter
    if (filters.search.trim()) {
  const search = filters.search.trim().toLowerCase();

  filtered = filtered.filter((c) => {
    const guardName = String(c.guard_name || "").toLowerCase();
    const routeName = String(c.route_name || "").toLowerCase();
    const guardId = String(c.guard_id || "").toLowerCase();
    const routeId = String(c.route_id || "").toLowerCase();

    return (
      guardName.includes(search) ||
      routeName.includes(search) ||
      guardId.includes(search) ||
      routeId.includes(search)
    );
  });
}

    // Check type filter
    if (filters.checkType) {
      filtered = filtered.filter((c) => c.check_type === filters.checkType);
    }

    // Date range filter
    if (filters.startDate) {
      const start = new Date(filters.startDate);
      filtered = filtered.filter((c) => new Date(c.check_time) >= start);
    }
    if (filters.endDate) {
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59);
      filtered = filtered.filter((c) => new Date(c.check_time) <= end);
    }

    setFilteredChecks(filtered);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

 const resetFilters = () => {
  setFilters({
    search: "",
    checkType: "",
    startDate: "",
    endDate: "",
  });

  setFilteredChecks(checks);
  setCurrentPage(1);

  toast("Filters reset", {
    icon: "ℹ️",
  });
};

  const handleRefresh = () => {
    const token = getToken();
    fetchChecks(token);
  };

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/route-master" : "/route-master1"),
    [dashboardData.user_map]
  );

  // Pagination
  const totalPages = Math.ceil(filteredChecks.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentChecks = filteredChecks.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)] font-['Inter',system-ui,sans-serif]">
          Loading in-campus checks...
        </p>
      </div>
    );
  }

  return (
    <Layout dashboardData={dashboardData} routePage={routePage} activePage="in-campus-list">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "white",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "white",
            },
          },
          info: {
            iconTheme: {
              primary: "#3b82f6",
              secondary: "white",
            },
          },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-[22px]">
          <div>
            <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
              In-Campus Check List
            </h1>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              View all in-campus check records and their status
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/incampus")}
            className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
            style={{
              background: "var(--btn-bg)",
              color: "var(--btn-text)",
              boxShadow: "0 4px 18px var(--accent-glow)",
            }}
          >
            + Create New Check
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-[18px]">
          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--btn-bg)] border border-[var(--surface-border)] shadow-[0_4px_18px_var(--accent-glow)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--btn-text)] opacity-75 m-0 mb-1.5">
              Total Checks
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--btn-text)]">
              {stats.total}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Random Checks
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {stats.random}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Schedule Checks
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {stats.schedule}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Today's Checks
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {stats.today}
            </p>
          </div>
        </div>

        {/* Filters Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap mb-4">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Filters
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Search and filter in-campus check records
              </p>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleRefresh}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)]"
              >
                Refresh
              </button>
              <button
                type="button"
                onClick={resetFilters}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)]"
              >
                Reset
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Search
              </label>
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search by guard or route..."
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
              />
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Check Type
              </label>
              <select
                name="checkType"
                value={filters.checkType}
                onChange={handleFilterChange}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
              >
                <option value="">All Types</option>
                <option value="random">Random</option>
                <option value="schedule">Schedule</option>
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
              />
            </div>

            <div>
              <label className="block text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="px-6 py-4 border-b border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-[17px] font-extrabold text-[var(--text-primary)] m-0">
                In-Campus Check Records
              </h3>
              <p className="text-[12px] text-[var(--text-muted)] mt-1">
                Complete list of all in-campus check records
              </p>
            </div>
            <span className="text-[13px] text-[var(--text-muted)]">
              {filteredChecks.length} records
            </span>
          </div>

          <div className="overflow-x-auto">
            {currentChecks.length === 0 ? (
              <div className="text-center py-12 text-[var(--text-muted)] text-[13px]">
                No in-campus checks found
              </div>
            ) : (
              <table className="w-full min-w-[800px] text-[13px] font-['Inter',system-ui,sans-serif]">
                <thead>
                  <tr style={{ background: "var(--btn-bg)" }}>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">
                      #
                  </th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">
                      Guard
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">
                      Route
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">
                      Check Time
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentChecks.map((check, index) => (
                    <tr
                      key={check.id || index}
                      className={`border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors ${
                        index % 2 === 0 ? "bg-[var(--card-bg)]" : "bg-[var(--surface)]"
                      }`}
                    >
                      <td className="px-4 py-3 text-[13px] text-[var(--text-muted)]">
                        {startIndex + index + 1}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="font-extrabold text-[var(--text-primary)]">
                          {check.guard_name || "—"}
                        </div>
                        <div className="text-[12px] text-[var(--text-muted)]">
                          ID: {check.guard_id || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <div className="font-semibold text-[var(--text-primary)]">
                          {check.route_name || "—"}
                        </div>
                        <div className="text-[12px] text-[var(--text-muted)]">
                          ID: {check.route_id || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span
                          className={`px-3 py-1 rounded-full text-[11px] font-extrabold ${
                            check.check_type === "random"
                              ? "bg-[rgba(59,130,246,0.12)] border border-[rgba(59,130,246,0.28)] text-[#3b82f6]"
                              : "bg-[rgba(139,92,246,0.12)] border border-[rgba(139,92,246,0.28)] text-[#7c3aed]"
                          }`}
                        >
                          {check.check_type || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[var(--text-primary)] align-middle">
                        {formatISTDateTime(check.check_time)}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span
                          className={`px-3 py-1 rounded-full text-[11px] font-extrabold ${
                            check.status === "completed"
                              ? "bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.28)] text-[#22c55e]"
                              : check.status === "pending"
                              ? "bg-[rgba(245,158,11,0.12)] border border-[rgba(245,158,11,0.28)] text-[#f59e0b]"
                              : check.status === "missed"
                              ? "bg-[rgba(239,68,68,0.12)] border border-[rgba(239,68,68,0.28)] text-[#ef4444]"
                              : "bg-[var(--surface)] text-[var(--text-muted)]"
                          }`}
                        >
                          {check.status || "pending"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {filteredChecks.length > 0 && (
            <div className="px-6 py-4 border-t border-[var(--divider)] flex items-center justify-between gap-3 flex-wrap">
              <div className="text-[12px] text-[var(--text-muted)]">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredChecks.length)} of{" "}
                {filteredChecks.length} entries
              </div>

              <div className="flex gap-1.5 flex-wrap">
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--accent-faint)] transition-colors"
                >
                  Previous
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    type="button"
                    onClick={() => goToPage(page)}
                    className={`border rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer transition-colors ${
                      currentPage === page
                        ? "bg-[var(--btn-bg)] text-[var(--btn-text)] border-[var(--accent-border)]"
                        : "border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] hover:bg-[var(--accent-faint)]"
                    }`}
                  >
                    {page}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-3 py-2 text-[12px] font-extrabold cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--accent-faint)] transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}